import { localStorageSupported } from '#lib/helper/browser.js';
import { type ListStore } from '#lib/stores/infinity-list.js';
import { unreadReviewsCountStore } from '#lib/stores/review.js';
import {
	apiAppNotificationNotificationGetstats,
	apiAppNotificationNotificationMarkasread,
	apiAppNotificationNotificationMarkasunread,
	type Notification
} from '#lib/types/generated/index.js';
import { get, writable } from 'svelte/store';
import { adminStore } from './admin';

export const notificationPanelStore = writable<boolean>(false);

export const notificationStatsStore = writable<{
	unread: number;
	knownNotificationsIds: number[];
}>({ unread: 0, knownNotificationsIds: [] });

export function loadNotificationStatsFromData(stats: {
	unread: number;
	unreadReviews: number;
}): void {
	notificationStatsStore.set({
		unread: stats.unread,
		knownNotificationsIds: get(notificationStatsStore).knownNotificationsIds
	});
	unreadReviewsCountStore.set(stats.unreadReviews);
}

export function refreshNotificationStats(): Promise<void> {
	return apiAppNotificationNotificationGetstats().then((response) => {
		loadNotificationStatsFromData(response);
	});
}

export const mutedDeviceStore = writable<boolean>(false);

mutedDeviceStore.subscribe((value) => {
	if (!localStorageSupported()) {
		return;
	}

	if (value) {
		localStorage.setItem('muted', 'true');
	} else {
		localStorage.setItem('muted', 'false');
	}
});

export const notificationStore = writable<ListStore<Notification>>({
	data: [],
	isFresh: true,
	apiSearchUrl: '/notification/search'
});

export function markNotificationAsRead(notification: Notification): void {
	if (get(adminStore)) return;
	notificationStore.update((store) => {
		notification.isRead = true;
		notificationStatsStore.update((value) => {
			return {
				unread: value.unread - 1,
				knownNotificationsIds: value.knownNotificationsIds
			};
		});

		apiAppNotificationNotificationMarkasread({ id: String(notification.id) }).catch(() => {
			notification.isRead = false;
			notificationStatsStore.update((value) => {
				return {
					unread: value.unread + 1,
					knownNotificationsIds: value.knownNotificationsIds
				};
			});
		});

		return store;
	});
}

export function markNotificationAsUnread(notification: Notification): void {
	notificationStore.update((store) => {
		notification.isRead = false;
		notificationStatsStore.update((value) => {
			return {
				unread: value.unread + 1,
				knownNotificationsIds: value.knownNotificationsIds
			};
		});

		apiAppNotificationNotificationMarkasunread({ id: String(notification.id) }).catch(() => {
			notification.isRead = true;
			notificationStatsStore.update((value) => {
				return {
					unread: value.unread - 1,
					knownNotificationsIds: value.knownNotificationsIds
				};
			});
		});
		return store;
	});
}

export function updateNotificationReadState(notificationId: number, isRead: boolean): void {
	notificationStore.update((store) => {
		const notification = store.data.find((n) => n.id === notificationId);
		if (notification) {
			notification.isRead = isRead;
		}
		return store;
	});
}

export function markAllNotificationsAsRead(): void {
	notificationStore.update((store) => {
		store.data.forEach((notification) => {
			notification.isRead = true;
		});
		const currentUnread = get(notificationStatsStore).unread;

		notificationStatsStore.update((value) => {
			return { unread: 0, knownNotificationsIds: value.knownNotificationsIds };
		});

		apiAppNotificationNotificationMarkasread({}).catch(() => {
			store.data.forEach((notification) => {
				notification.isRead = false;
			});
			notificationStatsStore.update((value) => {
				return {
					unread: currentUnread,
					knownNotificationsIds: value.knownNotificationsIds
				};
			});
		});

		return store;
	});
}

export function removeNotification(notification: Notification): void {
	notificationStore.update((store) => {
		const index = store.data.findIndex((n) => n.id === notification.id);
		if (index !== -1) {
			store.data.splice(index, 1);
		}
		return store;
	});
}

export function removeAllNotifications(): void {
	notificationStore.update((store) => {
		store.data = [];
		return store;
	});
}
